import Bill from "../../../classes/Bill";
import Sale from "../../../classes/Sale";
import { db } from "../../../boot/firebase";
import {
  collection,
  doc,
  deleteField,
  writeBatch,
  documentId,
} from "firebase/firestore";
import { isBoolean } from "lodash";

export const GET_BILL = async (
  { dispatch },
  { projectID, saleID, orderID, billID }
) => {
  return dispatch("GET_DOC", {
    converter: Bill,
    path: Bill.getPath(projectID, saleID, orderID, billID),
    stateKey: "bills",
    id: billID,
  });
};

export const GET_BILLS = (
  { dispatch },
  { projectID, saleID, orderID, conditions, limitCount, pagination }
) => {
  return dispatch("GET_COLLECTION", {
    converter: Bill,
    stateKey: "bills",
    path:
      projectID && saleID && orderID
        ? Bill.getPath(projectID, saleID, orderID)
        : null,
    conditions,
    limitCount,
    pagination,
  });
};

export const LISTEN_BILL = async (
  { dispatch },
  { projectID, saleID, orderID, billID, next, error, unlisten }
) => {
  return dispatch("LISTEN_DOC", {
    converter: Bill,
    path: Bill.getPath(projectID, saleID, orderID, billID),
    stateKey: "bills",
    id: billID,
    next,
    error,
    unlisten,
  });
};

export const LISTEN_BILLS = (
  { dispatch },
  {
    projectID,
    saleID,
    orderID,
    conditions,
    listenerPath,
    error,
    next,
    unlisten,
  }
) => {
  const path =
    projectID && saleID && orderID
      ? Bill.getPath(projectID, saleID, orderID)
      : null;
  return dispatch("LISTEN_COLLECTION", {
    converter: Bill,
    path,
    colGroup: conditions || !path ? "bills" : null,
    stateKey: "bills",
    listenerPath,
    conditions,
    next,
    error,
    unlisten,
  });
};

export const LISTEN_SALE_BILLS = (
  { dispatch },
  { projectID, saleID, where, listenerPath, error, next, unlisten }
) => {
  return dispatch("LISTEN_COLLECTION", {
    converter: Bill,
    colGroup: "bills",
    stateKey: "bills",
    listenerPath: listenerPath || "sale-bills-" + projectID + saleID,
    pagination: {
      sortBy: documentId(),
      startAt: Sale.getPath(projectID, saleID),
      endAt: Sale.getPath(projectID, saleID) + "\uf8ff",
    },
    where,
    next,
    error,
    unlisten,
  });
};

export const SAVE_BILL = async (
  { dispatch, state },
  { projectID, saleID, orderID, bill, isFinalBill, finalForOrder }
) => {
  const batch = writeBatch(db);

  const saveDocuments = (existingData) => {
    return dispatch("SAVE_DOCUMENTS", {
      batch,
      parentData: bill.data,
      fieldPath: "document",
      folderPath: bill.path,
      isSingle: true,
      existingData,
    });
  };

  const updateSale = async (changes = {}) => {
    if (isBoolean(isFinalBill)) {
      const ackChanges = {
        [`status.acks.${bill.ackID}.fullyBilled`]: isFinalBill,
      };
      // Final bill for the WHOLE order: flip the order's sibling acks too.
      // Bills attach to a single ack, so when an order's bills all land on
      // one ack the siblings otherwise stay unbilled forever — holding the
      // sale at "Awaiting bill". Enumerated from the freshly fetched sale so
      // only entries that exist are written (a dot-path write to a deleted
      // ack's id would recreate it as an orphan map entry).
      if (isFinalBill === true && finalForOrder) {
        const sale = await dispatch("GET_SALE", { projectID, saleID });
        Object.entries(sale?.data?.status?.acks || {}).forEach(
          ([ackID, entry]) => {
            if (entry && entry.orderID === orderID && !entry.fullyBilled) {
              ackChanges[`status.acks.${ackID}.fullyBilled`] = true;
            }
          }
        );
      }
      return dispatch("UPDATE_SALE_STATUS", {
        batch,
        projectID,
        saleID,
        changes: {
          ...changes,
          ...ackChanges,
        },
      });
    }
  };

  if (!bill.id) {
    // New Bill
    bill.data.created = new Date();
    bill.data.createdBy = state.user.id;

    const ref = doc(collection(db, Bill.getPath(projectID, saleID, orderID)));
    bill.id = ref.id;

    await saveDocuments();
    batch.set(ref.withConverter(Bill), bill);

    // Lock Acknowledgement — credit memos shouldn't lock the ack since they
    // don't represent receipt of goods/services for that PO line.
    if (!bill.data.creditMemo) {
      const ack = await dispatch("GET_ACK", {
        projectID,
        saleID,
        orderID,
        ackID: bill.ackID,
      });
      if (!ack.data.locked) {
        await dispatch("UPDATE_ACK", {
          projectID,
          saleID,
          orderID,
          ackID: bill.ackID,
          changes: { locked: true },
        });
      }
    }

    await updateSale({
      [`status.bills.${bill.id}`]: {
        orderID,
        ackID: bill.ackID,
        fullyPaid: bill.data.balance === 0,
      },
    });
    await batch.commit();
  } else {
    // Existing Bill
    bill.data.modified = new Date();
    bill.data.modifiedBy = state.user.id;
    const current = await dispatch("GET_BILL", {
      projectID,
      saleID,
      orderID,
      billID: bill.id,
    });

    await saveDocuments(current.data);
    batch.set(doc(db, bill.path).withConverter(Bill), bill);
    await updateSale({
      [`status.bills.${bill.id}`]: {
        orderID,
        ackID: bill.ackID,
        fullyPaid: bill.data.balance === 0,
      },
    });
    await batch.commit();
  }
  return bill;
};

export const DELETE_BILL = async (
  { dispatch },
  { projectID, saleID, orderID, billID }
) => {
  const bill = await dispatch("GET_BILL", {
    projectID,
    saleID,
    orderID,
    billID,
  });
  if (!bill) {
    return;
  }
  // Defensive guard: refuse to delete a bill once any payment (or
  // credit-memo application) has reduced its balance. Mirrors
  // `Bill.canBeDeletedBy` so backend/script callers (and the
  // DELETE_ACK cascade) can't sneak around the UI gate. The thrown
  // error code is translated by `formatError` via `error.billHasPayments`.
  if (bill.hasPayments) {
    throw new Error("billHasPayments");
  }
  const batch = writeBatch(db);
  batch.delete(doc(db, bill.path));
  const changes = {
    [`status.bills.${billID}`]: deleteField(),
  };
  // Credits never finalize an ack's billing state, so deleting one shouldn't
  // reset the fullyBilled flag.
  if (!bill.data.creditMemo) {
    changes[`status.acks.${bill.data.ackID}.fullyBilled`] = false;
  }
  await dispatch("UPDATE_SALE_STATUS", {
    batch,
    projectID,
    saleID,
    changes,
  });
  return batch.commit();
};
